import { useEffect, useMemo, useState } from 'react'
import parseHtml from 'html-react-parser'
import _ from 'lodash'

import { Typography, Skeleton, TextField, Stack, Switch, RadioGroup, FormControlLabel, Radio, Button } from '@mui/material'

import { PassContext } from '/components/editor/pass-context'
import AppleLinkedAppPopup from '/components/editor/apple/popup-editor-linked-app'
import GoogleLinkedAppPopup from '/components/editor/google/popup-editor-linked-app'

import PDF417Img from '/assets/images/pdf417.png'
import Code128Img from '/assets/images/code128.png'
import QRCodeImg from '/assets/images/qrcode.png'
import AztecImg from '/assets/images/aztec.png'
import NoBarcodeImg from '/assets/images/no-barcode.png'

import useRequiredFields from '../config/required-fields'

const WizardStart = ({ passTypeData, setPrevButtonState, setNextButtonState, googlePassType, model, setModel, template, setTemplate, classData, setClassData, objectData, setObjectData, disableExpiredGooglePasses, setDisableExpiredGooglePasses, instanceData, setInstanceData }) => {

  useEffect(() => {
    setPrevButtonState("hidden")
    setNextButtonState("disabled")
  }, [])

  const [draftModel, setDraftModel] = useState(() => model ? _.cloneDeep(model) : {})
  const [draftTemplate, setDraftTemplate] = useState(() => template ? _.cloneDeep(template) : {})
  const [draftClassData, setDraftClassData] = useState(() => classData ? _.cloneDeep(classData) : {})
  const [draftObjectData, setDraftObjectData] = useState(() => objectData ? _.cloneDeep(objectData) : {})

  useEffect(() => {
    setDraftModel(model ? _.cloneDeep(model) : {})
  }, [model])

  useEffect(() => {
    setDraftTemplate(template ? _.cloneDeep(template) : {})
  }, [template])

  useEffect(() => {
    setDraftClassData(classData ? _.cloneDeep(classData) : {})
  }, [classData])

  useEffect(() => {
    setDraftObjectData(objectData ? _.cloneDeep(objectData) : {})
  }, [objectData])

  const requiredFields = useMemo(() => {
    return useRequiredFields({
      model: draftModel,
      setModel: setDraftModel,
      commitModel: setModel,
      template: draftTemplate,
      setTemplate: setDraftTemplate,
      commitTemplate: setTemplate,
      classData: draftClassData,
      setClassData: setDraftClassData,
      commitClassData: setClassData,
      objectData: draftObjectData,
      setObjectData: setDraftObjectData,
      commitObjectData: setObjectData,
      disableExpiredGooglePasses,
      setDisableExpiredGooglePasses,
      googlePassType
    })
  }, [draftModel, draftTemplate, draftClassData, draftObjectData, disableExpiredGooglePasses, googlePassType, setModel, setTemplate, setClassData, setObjectData])

  useEffect(() => {
    const ok = requiredFields?.every(field => {
      if (field.type !== "text") return true
      if (field.optional) return true
      return !!field.value?.length
    })

    setNextButtonState(ok ? "enabled" : "disabled")
  }, [requiredFields, setNextButtonState])


  if (passTypeData) return (
    <>
      <Typography variant="h3" sx={{ p: 2 }}>Pass Template Wizard</Typography>
      <InfoText wizardData={passTypeData.wizard_data} />
      <hr />
      <RequiredFields requiredFields={requiredFields} />
      <hr />
      <Barcode passTypeData={passTypeData} template={template} setTemplate={setTemplate} objectData={objectData} setObjectData={setObjectData} instanceData={instanceData} setInstanceData={setInstanceData} />
      <LinkedApps template={template} setTemplate={setTemplate} classData={classData} setClassData={setClassData} objectData={objectData} setObjectData={setObjectData} googlePassType={googlePassType} />
    </>
  )
}

export default WizardStart

const newLinesToBrTags = text => text ? text.replace(/\n/g, "<br/>") : ""

const InfoText = ({ wizardData }) => {

  if (wizardData.info) return (
    <Typography sx={{ p: 2 }}>
      {parseHtml(newLinesToBrTags(wizardData.info))}
      <br />
      {wizardData.info && <>Further edits can be carried out using the editor once the wizard completes.</>}
    </Typography>
  )

  return <Skeleton variant="text" width="100%" height={300} />
}


const RequiredFields = ({ requiredFields }) => {

  return (
    <>
      <Typography variant="h4" sx={{ p: 2 }}>First, please provide the following required values</Typography>

      <div className='flex flex-col gap-10 w-full p-5'>

        {requiredFields?.map(field => {

          switch (field.type) {
            case "text": return <TextFieldWithHelp key={field.name} field={field} />
            case "radio": return <RadioGroupWithHelp key={field.name} field={field} />
            case "switch": return <SwitchWithHelp key={field.name} field={field} />
          }
        })}

      </div>
    </>
  )
}

const TextFieldWithHelp = ({ field }) => (
  <TextField
    fullWidth
    value={field.value ?? ""}
    label={field.name}
    onChange={e => field.setValue(field, e.target.value)}
    onBlur={e => field.setValue(field, e.target.value, { commit: true })}
    slotProps={{ inputLabel: { shrink: true } }}
    type="search"
    helperText={field.help}
  />
)

const SwitchWithHelp = ({ field }) => (
  <div className='flex flex-col w-full'>
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Typography>{field.name} : No</Typography>
      <Switch checked={Boolean(field.value)} onChange={e => field.setValue(field, e.target.checked, { commit: true })} />
      <Typography>Yes</Typography>
    </Stack>
    <div className='text-sm italic'>{field.help}</div>
  </div>
)

const RadioGroupWithHelp = ({ field }) => (
  <div className='flex flex-col w-full'>
    <div>{field.name}
      <RadioGroup
        row
        value={field.value ?? ""}
        onChange={e => field.setValue(field,e.target.value, { commit: true })}
      >
        {field.options.map(o => <FormControlLabel key={o.value} value={o.value} control={<Radio />} label={o.label} />)}

      </RadioGroup>
    </div>
    <div className='text-sm italic'>{field.help}</div>
  </div>
)


const LinkedApps = ({ template, setTemplate, classData, setClassData, objectData, setObjectData, googlePassType }) => {

  const [appleOpen, setAppleOpen] = useState(false)
  const [googleOpen, setGoogleOpen] = useState(false)

  const appLinkData = useMemo(() => {
    if (googlePassType === "generic") {
      if (objectData) return objectData.appLinkData
    } else {
      if (classData) return classData.appLinkData
    }
  }, [googlePassType, objectData, classData])

  return (
    <>
      <div className='flex flex-col gap-5 w-full mt-10 mb-10'>
        <Typography variant="h4" sx={{ p: 2 }}>Would you like to add a link to a native app?</Typography>

        <div className='flex flex-row justify-between items-center'>
          <div className='flex flex-col gap-5'>
            <div><Typography variant="h5" sx={{ p: 2 }}>Apple IOS</Typography></div>
            <div><Typography variant="h5" sx={{ p: 2 }}>Google Android</Typography></div>
          </div>

          <div className='flex flex-col gap-5'>
            <img src={template.meta?.appLink?.icon} className="app-link-logo" />
            <img src={appLinkData?.icon} className="app-link-logo" style={{ width: 70 }} />
          </div>
          <div className='flex flex-col gap-5'>
            <div>{template.meta?.appLink?.seller}<br />{template.meta?.appLink?.title}</div>
            <div>{appLinkData?.seller}<br />{appLinkData?.title}</div>
          </div>
          <div className='flex flex-col gap-5'>
            <div>ID: {template.meta?.appLink?.id ? template.meta?.appLink?.id : "no app assigned"}</div>
            <div>ID: {appLinkData?.androidAppLinkInfo?.packageName ? appLinkData?.androidAppLinkInfo?.packageName : "no app assigned"}</div>
          </div>

          <div className='flex flex-col gap-5'>
            <Button variant="contained" onClick={() => setAppleOpen(true)}>Select Apple App</Button>
            <Button variant="contained" onClick={() => setGoogleOpen(true)}>Select Google App</Button>
          </div>
        </div>
      </div>

      <PassContext.Provider value={{ template, setTemplate, classData, setClassData, objectData, setObjectData, passType: googlePassType }}>
        <AppleLinkedAppPopup open={appleOpen} close={() => setAppleOpen(false)} />
        <GoogleLinkedAppPopup open={googleOpen} close={() => setGoogleOpen(false)} />
      </PassContext.Provider>

    </>
  )
}


const googleBarcode = {
  PKBarcodeFormatQR: "QR_CODE",
  PKBarcodeFormatAztec: "AZTEC",
  PKBarcodeFormatPDF417: "PDF_417",
  PKBarcodeFormatCode128: "CODE_128",
  NONE: "NONE"
}

const Barcode = ({ passTypeData, template, setTemplate, objectData, setObjectData, instanceData, setInstanceData }) => {

  const [showText, setShowText] = useState(instanceData.barcode?.__text__ !== "")

  const setBarcode = type => {
    let t = _.cloneDeep(template)
    let o = _.cloneDeep(objectData)
    let i = _.cloneDeep(instanceData)

    t.barcode = {
      altText: "{{__barcode__.__text__}}",
      format: type,
      message: showText ? "{{__barcode__.__value__}}" : ""
    }

    o.barcode = {
      alternateText: "{{__barcode__.__text__}}",
      type: googleBarcode[type],
      value: showText ? "{{__barcode__.__value__}}" : ""
    }

    i.__barcode__ = {
      __text__: showText ? "Barcode text" : "",
      __value__: "Barcode payload"
    }

    setTemplate(t)
    setObjectData(o)
    setInstanceData(i)
  }

  const setBarcodeText = show => {
    setShowText(show)

    let i = _.cloneDeep(instanceData)
    i.__barcode__.__text__ = show ? "Barcode text" : ""
    setInstanceData(i)
  }

  const barCodeValue = useMemo(() => {
    if (template.barcode) return template.barcode.format
    return "NONE"
  }, [template])

  if (!passTypeData.smartix_scanner) return (
    <>
      <div className='flex flex-col gap-5 w-full mt-10 mb-10'>
        <Typography variant="h4" sx={{ p: 2 }}>Barcodes</Typography>

        <RadioGroup
          row
          value={barCodeValue}
          onChange={e => setBarcode(e.target.value)}
        >
          <FormControlLabel value="PKBarcodeFormatQR" control={<Radio />} label={<QrSelector format="QR Code" />} labelPlacement="top" />
          <FormControlLabel value="PKBarcodeFormatAztec" control={<Radio />} label={<QrSelector format="Aztec" />} labelPlacement="top" />
          <FormControlLabel value="PKBarcodeFormatPDF417" control={<Radio />} label={<QrSelector format="PDF 417" />} labelPlacement="top" />
          <FormControlLabel value="PKBarcodeFormatCode128" control={<Radio />} label={<QrSelector format="Code 128" />} labelPlacement="top" />
          <FormControlLabel value="NONE" control={<Radio />} label={<QrSelector format="No Barcode" />} labelPlacement="top" />

        </RadioGroup>
        {barCodeValue !== "NONE" &&
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography>Show text below barcode? : No</Typography>
            <Switch checked={showText} onChange={e => setBarcodeText(e.target.checked)} />
            <Typography>Yes</Typography>
          </Stack>}

      </div>
      <hr/>
    </>
  )
}

const barcodeImages = {
  "QR Code": QRCodeImg,
  "Aztec": AztecImg,
  "PDF 417": PDF417Img,
  "Code 128": Code128Img,
  "No Barcode": NoBarcodeImg
}

const QrSelector = ({ format }) => (
  <span className='flex flex-col gap-5'>
    <img src={barcodeImages[format]} style={{ height: 100, backgroundColor: "white" }} />
    <div>{format}</div>
  </span>
)
